import ReactiveDialog from "../../common/ReactiveDialog";
import { Box, Typography } from "@mui/material";
import {
  useGetFamilyDetails,
  useReactivateFamily,
} from "../../../services/advisor";
import Loading from "../../common/Loading";
import { PlanUtilization } from "protogen/common_pb";
import { useEffect, useState } from "react";
import AdvisorSelect from "components/creation/AdvisorSelect";
import PlatformFeeSelect from "../PlatformFeeSelect";
import { PlatformFeeSchedule } from "protogen/billing_pb";
import { BillingProduct } from "protogen/billing_pb";
import ProductSelect from "../ProductSelect";
import CouponSelect from "../CouponSelect";
import { DatePicker } from "@mui/x-date-pickers";
import { BillingCoupon } from "protogen/billing_pb";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: () => void;
};

type ReactivationContentProps = {
  planUtilization?: PlanUtilization;
  onStartDateChange: (date: string) => void;
  onAdvisorChange: (advisorRef: string) => void;
  onPlatformFeeChange: (fee: PlatformFeeSchedule) => void;
  onProductChange: (product: BillingProduct) => void;
  onCouponChange: (coupon?: BillingCoupon) => void;
  selectedStartDate: string;
  selectedAdvisorRef: string;
  selectedPlatformFee: PlatformFeeSchedule;
  selectedProduct?: BillingProduct;
  selectedCoupon?: BillingCoupon;
  disabled?: boolean;
};

const ReactivationContent = ({
  onStartDateChange,
  onAdvisorChange,
  onPlatformFeeChange,
  onProductChange,
  onCouponChange,
  selectedStartDate,
  selectedAdvisorRef,
  selectedPlatformFee,
  selectedProduct,
  selectedCoupon,
  disabled = false,
}: ReactivationContentProps) => {
  const [dateOpen, setDateOpen] = useState(false);
  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Typography variant="body" color="textSecondary">
        Re-activating this family will restore their service with the below
        configuration. Their status will be set to "pre-activation" and the
        advisor will have to confirm a start date before re-activating billing.
      </Typography>

      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          Assigned Advisor
        </Typography>
        <AdvisorSelect
          title="Select advisor"
          disabled={disabled}
          selectedAdvisor={selectedAdvisorRef}
          onChange={(r) => onAdvisorChange(r || "")}
        />
      </Box>

      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          New Start Date
        </Typography>
        <DatePicker
          sx={{
            width: "100%",
            ".MuiButtonBase-root": { marginRight: "unset" },
          }}
          label="Starting date"
          defaultValue={new Date(selectedStartDate)}
          disabled={disabled}
          onChange={(date) => {
            if (date === null) return;
            onStartDateChange(date.toISOString().split("T")[0]);
          }}
          format="MM/dd/yyyy"
          open={dateOpen}
          onClose={() => setDateOpen(false)}
          slotProps={{
            textField: {
              onClick: () => !disabled && setDateOpen(true),
            },
          }}
        />
      </Box>

      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          Service Plan
        </Typography>
        <ProductSelect
          value={selectedProduct}
          onUpdate={(p) => p && onProductChange(p)}
          disabled={disabled}
          stacked={false}
          isAdmin={true}
          widthPercentage={100}
          initialProductName={selectedProduct?.name}
          fullWidth
        />
      </Box>
      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          Platform Fee Structure
        </Typography>
        <PlatformFeeSelect
          value={selectedPlatformFee}
          onUpdate={(p) => p && onPlatformFeeChange(p)}
          disabled={disabled}
          fullWidth
        />
      </Box>
      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          Discount
        </Typography>
        <CouponSelect
          value={selectedCoupon}
          onUpdate={(c) => onCouponChange(c)}
          disabled={disabled}
          stacked={false}
          isAdmin={true}
          widthPercentage={100}
        />
      </Box>
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();

  const [selectedStartDate, setSelectedStartDate] = useState<string>("");
  const [selectedAdvisorRef, setSelectedAdvisorRef] = useState<string>("");
  const [selectedPlatformFee, setSelectedPlatformFee] =
    useState<PlatformFeeSchedule>(
      PlatformFeeSchedule.SCHEDULE_LEAD_GEN_35_35_35_20,
    );
  const [selectedProduct, setSelectedProduct] = useState<BillingProduct>();
  const [selectedCoupon, setSelectedCoupon] = useState<
    BillingCoupon | undefined
  >(undefined);

  const { request: requestReactivate, loading } = useReactivateFamily();

  useEffect(() => {
    if (open) {
      requestFamily({ familyRef });
      // Set default start date to today
      const today = new Date().toISOString().split("T")[0];
      setSelectedStartDate(today);
    }
  }, [open, familyRef]);

  useEffect(() => {
    if (familyData?.family) {
      // Pre-populate with existing family data
      setSelectedAdvisorRef(familyData.family.advisorRef);
      if (familyData.family.billingInfo?.product) {
        setSelectedProduct(familyData.family.billingInfo.product);
      }
    }
  }, [familyData]);

  const onReactivation = async () => {
    if (loading || !familyData?.family || !selectedProduct) return;

    const resp = await requestReactivate({
      familyRef,
      startDate: selectedStartDate,
      advisorRef: selectedAdvisorRef,
      platformFeeSchedule: selectedPlatformFee,
      product: selectedProduct,
      introCoupon: selectedCoupon,
      nullifyCoupon: !selectedCoupon,
    });

    if (resp?.family) {
      onUpdate();
    }
  };

  const isFormValid = !!(
    selectedStartDate &&
    selectedAdvisorRef &&
    selectedPlatformFee &&
    selectedProduct &&
    familyData?.family &&
    !loading &&
    !loadingFamily
  );

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Re-activate Family"
      primaryActionName="Re-activate"
      primaryActionEnabled={isFormValid}
      primaryAction={onReactivation}
      primaryActionColor="primary"
      fullWidthSize="sm"
    >
      {loadingFamily ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family ? (
        <ReactivationContent
          planUtilization={familyData.planUtilization}
          onStartDateChange={setSelectedStartDate}
          onAdvisorChange={setSelectedAdvisorRef}
          onPlatformFeeChange={setSelectedPlatformFee}
          onProductChange={setSelectedProduct}
          onCouponChange={setSelectedCoupon}
          selectedStartDate={selectedStartDate}
          selectedAdvisorRef={selectedAdvisorRef}
          selectedPlatformFee={selectedPlatformFee}
          selectedProduct={selectedProduct}
          selectedCoupon={selectedCoupon}
          disabled={loading}
        />
      ) : null}
    </ReactiveDialog>
  );
};
